import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from 'src/entities/user/store/useAuthStore';
import { getBookings } from 'src/features/organization/api/getBookings';
import { getBookingTypes } from 'src/features/organization/api/getBookingTypes';
import { hasLocalDiscoveryBooking } from 'src/features/organization/hooks/useOrgOnboardingGate';
import { useProductById } from 'src/features/product/product-detail/hooks/useProductById';
import useProductQuotes from 'src/features/product/product-detail/hooks/useProductQuotes';
import { analyticsService } from 'src/services/analytics/analyticsService';
import { RFQ_BUILDER_CAVELA_DIRECT_SHOWN } from 'src/services/analytics/rfqBuilderEvents';
import { FF_CAVELA_DIRECT } from 'src/settings';
import { useFeatureFlag } from 'src/shared/hooks';
import type { Product, ProductById } from 'src/shared/types/product';
import type { OrderQuote } from 'src/shared/types/quote';
import { type Rfq, RfqSuitabilityDecision } from 'src/shared/types/rfq';

import getRfq from '../api/getRfq';
import { useCreateProduct } from './useCreateProduct';
import { useIsFirstProduct } from './useIsFirstProduct';

const DISCOVERY_BOOKING_TYPE_NAMES = [
  'Cavela Discovery (Platform)',
  'Cavela Discovery (Meta Ads)',
  'Cavela Discovery (Astris Outbound)',
  'Cavela Discovery (Growth DNA)'
];

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 90_000;

const AUTO_QUOTE_FAILURE_STATUSES = new Set([
  'unsupported',
  'classification_failed',
  'failed'
]);

interface UseRfqSourcingFlowParams {
  rfq: Rfq | undefined;
}

interface UseRfqSourcingFlowReturn {
  isSourcingStarted: boolean;
  /** Controls visibility of the Cavela Direct overlay. Explicit state avoids the
   * mutation-data-never-resets bug that arises from reading routeAutoQuoteResult.matched. */
  showCavelaDirectOverlay: boolean;
  productForCavelaDirect: ProductById | null;
  orderQuotesForCavelaDirect: OrderQuote[] | undefined;
  isFirstOrgProduct: boolean;
  showCavelaDirect: boolean;
  onStartSourcing: () => void;
  handleCavelaDirectDismiss: () => void;
  showPostRfqBookingScreen: boolean;
  postRfqCalendlyUrl: string;
  handlePostRfqBookingDismiss: () => void;
}

/**
 * Owns the async flow triggered when the user clicks "Start sourcing" / "Submit RFQ":
 *
 * 1. Publish the RFQ.
 * 2. Create the product. The backend runs Cavela Direct routing synchronously inside
 *    createProduct and, if matched, dispatches the auto-quote pipeline as a background
 *    task, returning auto_quote_task_id (non-null when dispatched).
 * 3. If auto_quote_task_id is present (Cavela Direct matched):
 *    a. Show the overlay immediately and poll GET /products/<id>/ every 3 seconds.
 *    b. On auto_quote_result === true → refresh queries, overlay shows results.
 *    c. On auto_quote_status in failure set → dismiss overlay immediately and navigate.
 * 4. If no task ID (not matched, or flag off) → navigate directly to the product page.
 */
export const useRfqSourcingFlow = ({
  rfq
}: UseRfqSourcingFlowParams): UseRfqSourcingFlowReturn => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const selectedOrganization = useAuthStore(
    (state) => state.selectedOrganization
  );
  const orgId = selectedOrganization?.id;

  const {
    data: { isFirst: isFirstOrgProduct }
  } = useIsFirstProduct();
  const { isFeatureEnabled: showCavelaDirect } =
    useFeatureFlag(FF_CAVELA_DIRECT);

  // Mirror reactive values into refs so async callbacks and setTimeout handlers always
  // read the current value, avoiding stale closures.
  const isFirstOrgProductRef = useRef(isFirstOrgProduct);

  isFirstOrgProductRef.current = isFirstOrgProduct;

  const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const productDataRef = useRef<Product | null>(null);
  const timePassedRef = useRef<boolean>(false);
  /** While true, `attemptNavigation` must not proceed (matched path until user dismisses). */
  const cavelaAutoQuoteMatchedRef = useRef(false);

  const [isSourcingStarted, setIsSourcingStarted] = useState(false);
  const isSourcingStartedRef = useRef(false);
  const [showCavelaDirectOverlay, setShowCavelaDirectOverlay] = useState(false);
  const [showPostRfqBookingScreen, setShowPostRfqBookingScreen] =
    useState(false);
  const [postRfqCalendlyUrl, setPostRfqCalendlyUrl] = useState<string>('');
  const showingBookingScreenRef = useRef(false);
  const [cavelaDirectOverlayProductId, setCavelaDirectOverlayProductId] =
    useState<string | null>(null);
  /** True once the auto-quote completes; guards the overlay until the product refetch catches up. */
  const [cavelaOverlayPendingQuote, setCavelaOverlayPendingQuote] =
    useState(false);
  const [isPollingProduct, setIsPollingProduct] = useState(false);

  const { data: overlayProductById, isError: isOverlayProductByIdError } =
    useProductById(cavelaDirectOverlayProductId ?? '', {
      refetchInterval: isPollingProduct ? POLL_INTERVAL_MS : false
    });
  const { data: orderQuotesForCavelaDirect } = useProductQuotes(
    cavelaDirectOverlayProductId ?? ''
  );

  const { mutate: createProduct } = useCreateProduct();

  const productForCavelaDirect = useMemo((): ProductById | null => {
    if (!cavelaDirectOverlayProductId || !overlayProductById) {
      return null;
    }
    if (!overlayProductById.auto_quote_result && !cavelaOverlayPendingQuote) {
      return null;
    }

    return overlayProductById;
  }, [
    cavelaDirectOverlayProductId,
    overlayProductById,
    cavelaOverlayPendingQuote
  ]);

  useEffect(() => {
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  const attemptNavigation = () => {
    if (cavelaAutoQuoteMatchedRef.current) {
      return;
    }
    if (showingBookingScreenRef.current) {
      return;
    }
    if (timePassedRef.current && productDataRef.current) {
      const productId = productDataRef.current.id;

      navigate(
        `/products/${productId}?tab=quotes${
          isFirstOrgProductRef.current ? '' : '&animate=true'
        }`
      );
    }
  };

  useEffect(() => {
    if (!isPollingProduct) {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }

      return;
    }

    pollingTimeoutRef.current = setTimeout(() => {
      cavelaAutoQuoteMatchedRef.current = false;
      setShowCavelaDirectOverlay(false);
      setCavelaDirectOverlayProductId(null);
      setCavelaOverlayPendingQuote(false);
      setIsPollingProduct(false);
      attemptNavigation();
    }, POLL_TIMEOUT_MS);

    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [isPollingProduct]);

  // React to product polling: detect success or failure from auto_quote_result / auto_quote_status.
  useEffect(() => {
    if (!isPollingProduct || !overlayProductById) return;

    if (overlayProductById.auto_quote_result) {
      setIsPollingProduct(false);
      setCavelaOverlayPendingQuote(true);
      void queryClient.invalidateQueries({
        queryKey: ['product-quotes', cavelaDirectOverlayProductId]
      });
    } else if (
      overlayProductById.auto_quote_status &&
      AUTO_QUOTE_FAILURE_STATUSES.has(overlayProductById.auto_quote_status)
    ) {
      setIsPollingProduct(false);
      cavelaAutoQuoteMatchedRef.current = false;
      setShowCavelaDirectOverlay(false);
      setCavelaDirectOverlayProductId(null);
      attemptNavigation();
    }
  }, [
    overlayProductById?.auto_quote_result,
    overlayProductById?.auto_quote_status,
    isPollingProduct
  ]);

  // Fall back to standard navigation when the overlay product fetch errors out.
  useEffect(() => {
    if (!cavelaDirectOverlayProductId || !isOverlayProductByIdError) {
      return;
    }
    cavelaAutoQuoteMatchedRef.current = false;
    setShowCavelaDirectOverlay(false);
    setCavelaDirectOverlayProductId(null);
    setCavelaOverlayPendingQuote(false);
    setIsPollingProduct(false);
    attemptNavigation();
  }, [cavelaDirectOverlayProductId, isOverlayProductByIdError]);

  const handleCavelaDirectDismiss = () => {
    cavelaAutoQuoteMatchedRef.current = false;
    setShowCavelaDirectOverlay(false);
    setCavelaDirectOverlayProductId(null);
    setCavelaOverlayPendingQuote(false);
    setIsPollingProduct(false);
    const productId = productDataRef.current?.id;

    if (!productId) {
      return;
    }
    navigate(`/products/${productId}?tab=quotes`);
  };

  const createProductAndNavigate = () => {
    if (!rfq?.rfq?.id) return;

    createProduct(
      { rfq: rfq.rfq.id },
      {
        onSuccess: async (data: Product) => {
          productDataRef.current = data;

          if (showCavelaDirect && data.auto_quote_task_id) {
            // Routing matched server-side — show the overlay immediately and start
            // polling the product endpoint for auto-quote completion.
            cavelaAutoQuoteMatchedRef.current = true;
            setCavelaDirectOverlayProductId(data.id);
            setShowCavelaDirectOverlay(true);
            setIsPollingProduct(true);
            analyticsService.trackEvent(RFQ_BUILDER_CAVELA_DIRECT_SHOWN, {
              rfq_id: rfq.rfq.id
            });

            return;
          }

          // Check if we should show the post-RFQ booking screen.
          // rfq_filter is set synchronously during createProduct on the backend,
          // so a fresh fetch retrieves the decision immediately.
          if (rfq?.rfq?.id && orgId) {
            try {
              // Short-circuit: skip all network calls if already booked locally
              const localBooked = hasLocalDiscoveryBooking(orgId);

              if (!localBooked) {
                const [freshRfqData, bookings, bookingTypes] =
                  await Promise.all([
                    queryClient.fetchQuery({
                      queryKey: ['rfq', String(rfq.rfq.id)],
                      queryFn: () => getRfq(String(rfq.rfq.id)),
                      staleTime: 0
                    }),
                    getBookings({
                      organizationId: orgId,
                      bookingTypeNames: DISCOVERY_BOOKING_TYPE_NAMES,
                      includeAllStatuses: true
                    }),
                    getBookingTypes()
                  ]);

                const rfqFilter = freshRfqData?.rfq?.rfq_filter;
                const hasBackendBooking = bookings.length > 0;

                if (
                  rfqFilter?.decision === RfqSuitabilityDecision.ACCEPT &&
                  !hasBackendBooking &&
                  isFirstOrgProductRef.current
                ) {
                  const discoveryType = bookingTypes.find((t) =>
                    DISCOVERY_BOOKING_TYPE_NAMES.includes(t.name)
                  );
                  const calendlyUrl =
                    discoveryType?.effective_calendly_url ?? '';

                  if (calendlyUrl) {
                    showingBookingScreenRef.current = true;
                    setPostRfqCalendlyUrl(calendlyUrl);
                    setShowPostRfqBookingScreen(true);

                    return;
                  } else {
                    console.warn(
                      '[post-rfq-booking] RFQ accepted but no discovery calendly URL found',
                      {
                        rfqId: rfq.rfq.id,
                        bookingTypesCount: bookingTypes.length,
                        discoveryType
                      }
                    );
                  }
                }
              }
            } catch (err) {
              console.error(
                '[post-rfq-booking] Error checking booking screen eligibility',
                err
              );
            }
          }

          attemptNavigation();
        }
      }
    );
  };

  const handlePostRfqBookingDismiss = () => {
    showingBookingScreenRef.current = false;
    setShowPostRfqBookingScreen(false);
    const productId = productDataRef.current?.id;

    if (productId) {
      navigate(
        `/products/${productId}?tab=quotes${
          isFirstOrgProductRef.current ? '' : '&animate=true'
        }`
      );
    }
  };

  const onStartSourcing = () => {
    if (isSourcingStartedRef.current) return;
    isSourcingStartedRef.current = true;

    // TODO: Replace with lifted state or context to avoid direct DOM mutation.
    const backToHomeLink = document.getElementById('back-to-home-link');

    if (backToHomeLink) {
      backToHomeLink.style.display = 'none';
    }

    setIsSourcingStarted(true);
    productDataRef.current = null;
    timePassedRef.current = false;
    cavelaAutoQuoteMatchedRef.current = false;
    setShowCavelaDirectOverlay(false);
    setCavelaDirectOverlayProductId(null);
    setCavelaOverlayPendingQuote(false);
    setIsPollingProduct(false);

    if (!rfq?.rfq?.id || !rfq?.rfq?.document?.name) return;

    navigationTimeoutRef.current = setTimeout(() => {
      timePassedRef.current = true;
      attemptNavigation();
    }, 3500);

    // Publishing an RFQ *is* creating its product (the backend derives
    // `published` from product existence); there is no separate publish call.
    createProductAndNavigate();
  };

  return {
    isSourcingStarted,
    showCavelaDirectOverlay,
    productForCavelaDirect,
    orderQuotesForCavelaDirect,
    isFirstOrgProduct,
    showCavelaDirect,
    onStartSourcing,
    handleCavelaDirectDismiss,
    showPostRfqBookingScreen,
    postRfqCalendlyUrl,
    handlePostRfqBookingDismiss
  };
};
